import { Paging, Result } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import dayjs from "dayjs";
import { AppProfileInjection } from "features/appProfileRegister/di/AppProfieInjection";
import { AppProfileRequestEntity } from "features/appProfileRegister/domain/entities/AppProfileRequestEntity";
import { AppProfileResponseEntity } from "features/appProfileRegister/domain/entities/AppProfileResponseEntity";
import { AppProfileVerificationEntity } from "features/appProfileRegister/domain/entities/AppProfileVerificationEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { downloadFileFromBlob } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

// ─── Input ───────────────────────────────────────────────────────────────────
class AppProfileInput implements Disposable {
  public fetchList = new BehaviorSubject<AppProfileRequestEntity>({
    page: 0,
    limit: 10,
    fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    toDate: dayjs().format("YYYY-MM-DD"),
  });
  public fetchDetail = new BehaviorSubject<{ id: string }>({ id: "" });
  public verifyProfile = new BehaviorSubject<AppProfileVerificationEntity & { contractId: string }>({} as any);
  public syncStatus = new BehaviorSubject<{ id: string }>({ id: "" });
  public syncAllStatus = new BehaviorSubject<boolean>(false);
  public exportExcel = new BehaviorSubject<any>(null);
  public downloadZip = new BehaviorSubject<AppProfileResponseEntity | null>(null);

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.verifyProfile.complete();
    this.syncStatus.complete();
    this.syncAllStatus.complete();
    this.exportExcel.complete();
    this.downloadZip.complete();
  }
}

// ─── Output ──────────────────────────────────────────────────────────────────
class AppProfileOutput implements Disposable {
  public profileList = new BehaviorSubject<AppProfileResponseEntity[]>([]);
  public profileDetail = new BehaviorSubject<AppProfileResponseEntity | null>(null);
  public paging = new BehaviorSubject<Paging | null>({ page: 0, limit: 10, total: 0 });
  public summary = new BehaviorSubject<Record<string, any>>({});
  public actionSuccess = new BehaviorSubject<'create' | 'update' | 'delete' | string | null>(null);
  public onError = new BehaviorSubject<string>('');
  public loading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public syncLoading = new BehaviorSubject<boolean>(false);
  public syncAllLoading = new BehaviorSubject<boolean>(false);
  public excelLoading = new BehaviorSubject<boolean>(false);
  public downloadLoading = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.profileList.complete();
    this.profileDetail.complete();
    this.paging.complete();
    this.summary.complete();
    this.actionSuccess.complete();
    this.onError.complete();
    this.loading.complete();
    this.detailLoading.complete();
    this.syncLoading.complete();
    this.syncAllLoading.complete();
    this.excelLoading.complete();
    this.downloadLoading.complete();
  }
}

// ─── ViewModel ───────────────────────────────────────────────────────────────
@injectable()
export class AppProfileViewModel extends BaseViewModel<AppProfileInput, AppProfileOutput> {
  private useCase = AppProfileInjection.getUseCase();

  constructor() {
    super(new AppProfileInput(), new AppProfileOutput());
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.output.loading.next(true);
        const res = await this.useCase.getProfileList(params);
        if (res?.data) {
          this.output.profileList.next(res.data);
          this.output.paging.next(res.pagination ?? null);
          this.output.summary.next(res.summary ?? {});
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async ({ id }) => {
      if (!id) return;
      try {
        this.output.detailLoading.next(true);
        const res = await this.useCase.getProfileDetail({ id });
        if (res?.data) {
          this.output.profileDetail.next(res.data);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.detailLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.verifyProfile, async (data) => {
      if (!data?.contractId) return;
      try {
        this.output.detailLoading.next(true);
        const res = await this.useCase.verifyProfile(data);
        if (res?.data) {
          this.output.actionSuccess.next(res.message || "Success");
          // Reload detail and list after verify
          this.input.fetchDetail.next({ id: data.contractId });
          this.input.fetchList.next(this.input.fetchList.getValue());
        }
      } catch (error: any) {
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      }
    });

    this.skipFirstInitial(this.input.syncStatus, async ({ id }) => {
      if (!id) return;
      try {
        this.output.syncLoading.next(true);
        const res = await this.useCase.syncStatus({ id });
        if (res?.data) {
          this.output.actionSuccess.next(res.message || "Success");
          this.input.fetchList.next(this.input.fetchList.getValue());
          const detailId = this.input.fetchDetail.getValue()?.id;
          if (detailId) this.input.fetchDetail.next({ id: detailId });
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.syncLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.syncAllStatus, async (trigger) => {
      if (!trigger) return;
      try {
        this.output.syncAllLoading.next(true);
        const res = await this.useCase.syncAllStatus();
        if (res) {
          this.output.actionSuccess.next(res.message || "Success");
          this.input.fetchList.next(this.input.fetchList.getValue());
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.syncAllLoading.next(false);
        this.input.syncAllStatus.next(false);
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (values) => {
      if (!values) return;
      try {
        this.output.excelLoading.next(true);
        const res = await this.useCase.exportExcel(values);
        if (res) {
          downloadFileFromBlob(
            res,
            `${strings.AppProfileRegister.Title} (${dayjs(values?.fromDate).format("DD/MM/YYYY")} - ${dayjs(values?.toDate).format("DD/MM/YYYY")}).xlsx`,
          );
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.excelLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.downloadZip, async (record) => {
      if (!record) return;
      try {
        this.output.downloadLoading.next(true);
        const res = await this.useCase.downLoadZip(record?.id ?? "");
        if (res) {
          downloadFileFromBlob(res, `${record?.code}_files.zip`);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.downloadLoading.next(false);
      }
    });
  }
}
